import React, { useState, useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
  Button,
  Collapse,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  InputLabel,
  FormControl,
  Chip,
  TextField,
  Autocomplete,
  Checkbox,
  useMediaQuery,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/Help";
import BetDetails from "./BetDetails";
import MobileBetDetails from "./MobileBetDetails";
import { LoadingIndicator } from "./LoadingIndicator";
import { ErrorHandler } from "./ErrorHandler";
import axios from "axios";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

const tierHierarchy = {
  Free: 0,
  Premium: 10,
  "Premium Plus": 20,
  Pro: 30,
};

const tierDisplayNames = {
  0: "Free",
  10: "Premium",
  20: "Plus",
  30: "Pro",
};

const hasAccess = (userTier, marketTier) => {
  if (!marketTier) return true;
  return tierHierarchy[userTier] >= parseInt(marketTier);
};

const renderTags = (value, getTagProps) =>
  value.length > 2
    ? [
        ...value

          .slice(0, 2)
          .map((option, index) => (
            <Chip {...getTagProps({ index })} label={option} size="small" />
          )),
        <Chip label={`+${value.length - 2} more`} size="small" />,
      ]
    : value.map((option, index) => (
        <Chip {...getTagProps({ index })} label={option} size="small" />
      ));

const Market = ({
  marketData,
  eventId,
  defaultExpanded,
  onFavoriteToggle,
  isFavorite,
  level,
  event,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [sortFields, setSortFields] = useState([]);
  const [availableSortFields, setAvailableSortFields] = useState([]);
  const [market, setMarket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playerFilter, setPlayerFilter] = useState([]);
  const [teamFilter, setTeamFilter] = useState([]);
  const [lineFilter, setLineFilter] = useState([]);
  const [minBookieCount, setMinBookieCount] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (defaultExpanded) {
      const element = document.getElementById(marketData.marketId);
      if (element) {
        const scrollToPosition =
          element.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({
          top: scrollToPosition,
          behavior: "smooth",
        });

        setTimeout(() => {
          setExpanded(true);
        }, 300);
      }
    } else {
      setExpanded(false);
    }
  }, [defaultExpanded, marketData.marketId]);

  const userHasAccess = useMemo(() => {
    return hasAccess(level, marketData.marketLevel);
  }, [level, marketData.marketLevel]);

  useEffect(() => {
    const fetchMarket = async () => {
      setLoading(true);
      try {
        const url =
          window.location.hostname === "localhost"
            ? `https://tool.bonusbank.com.au/api/v3/event/${eventId}/?markets=${marketData?.marketId}&testing&isMobile=${isMobile}`
            : `${window.location.origin}/wp-json/bonusbank/v1/event/${eventId}/?markets=${marketData?.marketId}&testing&isMobile=${isMobile}`;
        const response = await axios.get(url, {
          headers: {
            "content-type": "application/json",
            "X-WP-Nonce": window.nonce,
          },
        });
        if (response.data?.markets?.length > 0) {
          setMarket(response?.data?.markets[0]);
          setError(null);
        }
      } catch (err) {
        setError(err);
        setMarket(null);
      } finally {
        setLoading(false);
      }
    };

    if (expanded && userHasAccess) {
      fetchMarket();
    }
  }, [eventId, marketData, expanded, isMobile, userHasAccess]);

  const sortGroupedBets = (groupedBets, selectedFields) => {
    const sortedEntries = Object.entries(groupedBets).sort((a, b) => {
      const betDataA = a[1];
      const betDataB = b[1];

      for (const field of selectedFields) {
        if (field === "best / avg") {
          const oddsA = betDataA.maxAveRatio || 0;
          const oddsB = betDataB.maxAveRatio || 0;
          if (oddsA < oddsB) return 1;
          if (oddsA > oddsB) return -1;
        } else if (field === "best odds") {
          const oddsA = betDataA.maxOdds || 0;
          const oddsB = betDataB.maxOdds || 0;
          if (oddsA < oddsB) return -1;
          if (oddsA > oddsB) return 1;
        } else if (field === "avg odds") {
          const oddsA = betDataA.aveOdds || 0;
          const oddsB = betDataB.aveOdds || 0;
          if (oddsA < oddsB) return -1;
          if (oddsA > oddsB) return 1;
        } else if (field === "fair odds") {
          const oddsA = betDataA.fairOdds || 0;
          const oddsB = betDataB.fairOdds || 0;
          if (oddsA < oddsB) return -1;
          if (oddsA > oddsB) return 1;
        } else if (field === "line") {
          const lineA = parseFloat(betDataA.line) || 0;
          const lineB = parseFloat(betDataB.line) || 0;
          if (lineA < lineB) return -1;
          if (lineA > lineB) return 1;
        } else {
          const fieldA = betDataA[field] || "zzz";
          const fieldB = betDataB[field] || "zzz";
          if (fieldA < fieldB) return -1;
          if (fieldA > fieldB) return 1;
        }
      }
      return 0;
    });

    return Object.fromEntries(sortedEntries);
  };

  const processedMarket = useMemo(() => {
    if (!market) return null;

    const groupedBets = market.bets.groupedBets;
    const sortedGroupedBets =
      sortFields.length > 0
        ? sortGroupedBets(groupedBets, sortFields)
        : groupedBets;

    return {
      marketId: market.marketId,
      market: market.market,
      groupedBets: sortedGroupedBets,
      date: moment(market.date).local(),
      eventName: event,
      eventId: eventId,
      level,
    };
  }, [market, event, level, sortFields]);

  const getAvailableSortFields = (groupedBets) => {
    const fields = new Set();
    Object.values(groupedBets || {}).forEach((betData) => {
      if (betData.maxAveRatio) fields.add("best / avg");
      if (betData.maxOdds) fields.add("best odds");
      if (betData.aveOdds) fields.add("avg odds");
      if (betData.fairOdds != 0) fields.add("fair odds");
      if (betData.line) fields.add("line");
      if (betData.player) fields.add("player");
      if (betData.team) fields.add("team");
      if (betData.bet) fields.add("bet");
    });
    return Array.from(fields);
  };

  useEffect(() => {
    if (!market?.bets?.groupedBets) return;

    const availableFields = getAvailableSortFields(market.bets.groupedBets);
    setAvailableSortFields(availableFields);

    if (sortFields.length === 0) {
      const defaultFields = DEFAULT_SORT_ORDER.filter((field) =>
        availableFields.includes(field)
      );
      if (defaultFields.length > 0) {
        setSortFields(defaultFields);
      }
    }
  }, [market]);

  const uniquePlayers = useMemo(() => {
    const players = new Set();
    Object.values(processedMarket?.groupedBets || {}).forEach((betData) => {
      if (betData.player) {
        players.add(betData.player);
      }
    });
    return Array.from(players).sort();
  }, [processedMarket]);

  const uniqueTeams = useMemo(() => {
    const teams = new Set();
    Object.values(processedMarket?.groupedBets || {}).forEach((betData) => {
      if (betData.team) {
        teams.add(betData.team);
      }
    });
    return Array.from(teams).sort();
  }, [processedMarket]);

  const uniqueLines = useMemo(() => {
    const lines = new Set();
    Object.values(processedMarket?.groupedBets || {}).forEach((betData) => {
      if (betData.line) {
        lines.add(parseFloat(betData.line));
      }
    });
    return Array.from(lines).sort((a, b) => a - b);
  }, [processedMarket]);

  const DEFAULT_SORT_ORDER = ["team", "line", "bet", "player"];

  const handleSortFieldChange = (event) => {
    setSortFields(event.target.value);
  };

  const filteredGroupedBets = useMemo(() => {
    if (!processedMarket) return {};

    return Object.entries(processedMarket.groupedBets).filter(
      ([key, betData]) => {
        const isPlayerSelected =
          !playerFilter.length || playerFilter.includes(betData.player);
        const isTeamSelected =
          !teamFilter.length || teamFilter.includes(betData.team);
        const isLineSelected =
          !lineFilter.length || lineFilter.includes(parseFloat(betData.line));
        const hasMinBookies =
          minBookieCount === "" ||
          betData.bets.length >= parseInt(minBookieCount);
        const matchesSearch =
          searchQuery === "" ||
          String(key).toLowerCase().includes(searchQuery.toLowerCase());

        return (
          isPlayerSelected &&
          isTeamSelected &&
          isLineSelected &&
          hasMinBookies &&
          matchesSearch
        );
      }
    );
  }, [
    processedMarket,
    playerFilter,
    teamFilter,
    lineFilter,
    minBookieCount,
    searchQuery,
  ]);

  const renderContent = useMemo(() => {
    if (!processedMarket) return null;

    const filteredMarket = {
      ...processedMarket,
      groupedBets: Object.fromEntries(filteredGroupedBets),
    };

    return isMobile ? (
      <MobileBetDetails market={filteredMarket} />
    ) : (
      <BetDetails market={filteredMarket} />
    );
  }, [processedMarket, filteredGroupedBets, isMobile]);

  return (
    <Accordion
      key={marketData.marketId}
      id={marketData.marketId}
      expanded={expanded}
      onChange={() => setExpanded((prev) => !prev)}
      sx={{
        width: "100%",
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.text.primary,
        borderRadius: "12px !important",
        border: `1px solid ${theme.palette.divider}`,
        mb: 2,
        marginTop: "0px !important",
        transition: "all 0.2s ease-in-out",
        boxShadow: "none",
        "&:hover": {
          boxShadow: userHasAccess ? theme.shadows[2] : "none",
          transform: userHasAccess ? "translateY(-1px)" : "none",
        },
        "&:before": {
          display: "none",
        },
        cursor: "pointer",
        opacity: userHasAccess ? 1 : 0.85,
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              color: theme.palette.text.primary,
              transition: "transform 0.2s ease-in-out",
              transform: expanded ? "rotate(-180deg)" : "rotate(0)",
            }}
          />
        }
        aria-controls="panel1a-content"
        className="summary"
        id={`panel1a-header-${marketData.marketId}`}
        sx={{
          backgroundColor: theme.palette.eventBackground.main,
          color: theme.palette.text.primary,
          minHeight: "64px !important",
          height: "auto",
          padding: "8px 16px",
          "&.Mui-expanded": {
            minHeight: "64px !important",
            borderRadius: "12px 12px 0 0 !important",
          },
          "& > .MuiAccordionSummary-content": {
            margin: "8px 0",
            padding: "4px 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
            "&.Mui-expanded": {
              margin: "8px 0",
            },
          },
          "&:hover": {
            backgroundColor: userHasAccess
              ? theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)"
              : theme.palette.eventBackground.main,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: 1.4,
              flex: 1,
            }}
          >
            {marketData?.market}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            {!userHasAccess && <LockIcon sx={{ fontSize: "1rem" }} />}
            {userHasAccess && (
              <Tooltip
                title={
                  isFavorite ? "Remove from favorites" : "Add to favorites"
                }
              >
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onFavoriteToggle(marketData.marketId);
                  }}
                  size="small"
                  sx={{
                    color: isFavorite
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.1)",
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {isFavorite ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          backgroundColor: theme.palette.secondary.main,
          borderRadius: "0 0 12px 12px !important",
          color: theme.palette.text.primary,
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {userHasAccess ? (
          <>
            {loading && <LoadingIndicator />}
            <ErrorHandler error={error} />
            {!processedMarket && !loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "24px",
                  width: "100%",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.05)",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                  }}
                >
                  No data available for this market at the moment.
                </Typography>
              </Box>
            )}
            {!loading && !error && processedMarket && (
              <>
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: { xs: "flex-start", sm: "space-between" },
                      alignItems: "center",
                      width: "100%",
                      gap: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <FormControl
                        variant="outlined"
                        sx={{ minWidth: 120, width: "100%" }}
                      >
                        <InputLabel
                          id="sort-by-label"
                          sx={{ color: theme.palette.text.primary }}
                        >
                          Sort By
                        </InputLabel>
                        <Select
                          sx={{
                            width: { xs: "100%", sm: "200px" },
                            color: theme.palette.text.primary,

                            "& .MuiSvgIcon-root": {
                              color: theme.palette.text.primary,
                            },
                          }}
                          labelId="sort-by-label"
                          label="Sort By"
                          multiple
                          value={sortFields}
                          onChange={handleSortFieldChange}
                          renderValue={(selected) =>
                            selected
                              .map(
                                (field) =>
                                  field.charAt(0).toUpperCase() + field.slice(1)
                              )
                              .join(", ")
                          }
                        >
                          {availableSortFields.map((field) => (
                            <MenuItem key={field} value={field}>
                              {field.charAt(0).toUpperCase() + field.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip
                        title="Sort bets by selecting one or more fields. The sorting order is based on the selected fields from left to right, if the first field is equal it will sort by the second field and so on."
                        placement="right"
                      >
                        <IconButton sx={{ color: theme.palette.text.primary }}>
                          <HelpIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TextField
                      label="Quick Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      variant="outlined"
                      size="medium"
                      sx={{
                        width: { xs: "100%", sm: "200px" },
                        ml: { xs: 0, sm: 2 },
                      }}
                      InputProps={{
                        type: "search",
                      }}
                    />
                  </Box>

                  {isMobile && (
                    <Button
                      onClick={() =>
                        setShowAdvancedFilters(!showAdvancedFilters)
                      }
                      sx={{
                        width: "100%",
                        mt: 2,
                        mb: showAdvancedFilters ? 2 : 0,
                        textTransform: "none",
                      }}
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: showAdvancedFilters
                              ? "rotate(180deg)"
                              : "rotate(0)",
                            transition: "transform 0.2s",
                          }}
                        />
                      }
                    >
                      {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
                    </Button>
                  )}

                  <Collapse in={!isMobile || showAdvancedFilters}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: { xs: "space-between", sm: "flex-end" },
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                        mt: { xs: 0, sm: 2 },
                      }}
                    >
                      {uniquePlayers.length > 0 && (
                        <Autocomplete
                          multiple
                          sx={{ width: { xs: "100%", sm: "250px" } }}
                          options={uniquePlayers}
                          value={playerFilter}
                          onChange={(event, newValue) =>
                            setPlayerFilter(newValue)
                          }
                          disableCloseOnSelect
                          getOptionLabel={(option) => option}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox checked={selected} />
                              {option}
                            </li>
                          )}
                          renderTags={renderTags}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Player"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {params.InputProps.endAdornment}
                                    {playerFilter.length !==
                                      uniquePlayers.length && (
                                      <Button
                                        onClick={() =>
                                          setPlayerFilter(uniquePlayers)
                                        }
                                      >
                                        All
                                      </Button>
                                    )}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}

                      {uniqueTeams.length > 0 && (
                        <Autocomplete
                          multiple
                          sx={{ width: { xs: "100%", sm: "250px" } }}
                          options={uniqueTeams}
                          value={teamFilter}
                          onChange={(event, newValue) =>
                            setTeamFilter(newValue)
                          }
                          disableCloseOnSelect
                          getOptionLabel={(option) => option}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox checked={selected} />
                              {option}
                            </li>
                          )}
                          renderTags={renderTags}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Team"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {params.InputProps.endAdornment}
                                    {teamFilter.length !==
                                      uniqueTeams.length && (
                                      <Button
                                        onClick={() =>
                                          setTeamFilter(uniqueTeams)
                                        }
                                      >
                                        All
                                      </Button>
                                    )}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}

                      {uniqueLines.length > 0 && (
                        <Autocomplete
                          sx={{ width: { xs: "100%", sm: "250px" } }}
                          multiple
                          options={uniqueLines}
                          value={lineFilter}
                          onChange={(event, newValue) =>
                            setLineFilter(newValue)
                          }
                          disableCloseOnSelect
                          getOptionLabel={(option) => option.toString()}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox checked={selected} />
                              {option}
                            </li>
                          )}
                          renderTags={renderTags}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Line"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {params.InputProps.endAdornment}
                                    {lineFilter.length !==
                                      uniqueLines.length && (
                                      <Button
                                        onClick={() =>
                                          setLineFilter(uniqueLines)
                                        }
                                      >
                                        All
                                      </Button>
                                    )}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}

                      <TextField
                        type="number"
                        label="Min Bookies"
                        value={minBookieCount}
                        onChange={(e) => {
                          const value = e.target.value;
                          setMinBookieCount(
                            value === ""
                              ? ""
                              : Math.max(1, parseInt(value) || 1).toString()
                          );
                        }}
                        inputProps={{ min: 1 }}
                        sx={{ width: { xs: "100%", sm: "120px" } }}
                      />
                    </Box>
                  </Collapse>
                </Box>

                {renderContent}
              </>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 24px",
              width: "100%",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(145deg, ${theme.palette.secondary.dark}, rgba(0, 0, 0, 0.2))`
                  : `linear-gradient(145deg, ${theme.palette.secondary.light}, rgba(255, 255, 255, 0.9))`,
              borderRadius: "16px",
              flexDirection: "column",
              gap: 3,
              position: "relative",
              overflow: "hidden",
              boxShadow: "inset 0 0 20px rgba(0, 0, 0, 0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <LockIcon
              sx={{
                fontSize: "48px",
                color: theme.palette.primary.main,
                opacity: 0.8,
                mb: 1,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                textAlign: "center",
                fontWeight: 600,
                mb: 1,
              }}
            >
              Unlock {tierDisplayNames[marketData.marketLevel]} Markets
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                textAlign: "center",
                maxWidth: "500px",
                mb: 2,
              }}
            >
              Get access to exclusive {tierDisplayNames[marketData.marketLevel]}{" "}
              markets. To see a full list of available markets please{" "}
              <a
                href="https://bonusbank.com.au/market-coverage/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                }}
                onMouseOver={(e) =>
                  (e.target.style.textDecoration = "underline")
                }
                onMouseOut={(e) => (e.target.style.textDecoration = "none")}
              >
                click here
              </a>
              .
            </Typography>
            <Button
              component="a"
              href="https://bonusbank.com.au/membership-plans/"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="primary"
              startIcon={<LockOpenIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "28px",
                padding: "12px 32px",
                fontSize: "1.1rem",
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                boxShadow: theme.shadows[4],
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              Upgrade to {tierDisplayNames[marketData.marketLevel]}
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
export default Market;
